import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Alert, CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { contractService, projectService, contractorService } from '@smartgrow/shared';
import type { CreateContractRequest } from '@smartgrow/shared';
import { CurrencyInput } from '../../components/CurrencyInput';

export const NewContractPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [projectId, setProjectId] = useState('');
  const [form, setForm] = useState<CreateContractRequest>({
    contractorId: '',
    description: '',
    contractValue: 0,
    startDate: '',
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: contractorsData } = useQuery({
    queryKey: ['contractors-active'],
    queryFn: () => contractorService.getContractors({ size: 100 }),
  });

  const projects = projectsData?.content ?? [];
  const contractors = contractorsData?.content ?? [];

  const set = (field: keyof CreateContractRequest, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const mutation = useMutation({
    mutationFn: () => contractService.create(projectId, form),
    onSuccess: (c) => navigate(`/dashboard/contracts/${c.id}`),
    onError: () => setError('Failed to create contract. Check all required fields.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) { setError('Please select a project.'); return; }
    if (!form.contractorId) { setError('Please select a contractor.'); return; }
    if (!form.startDate) { setError('Start date is required.'); return; }
    setError('');
    mutation.mutate();
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text">
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold">New Contract</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Contract Details</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth required label="Project"
                value={projectId || ''}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {projects.map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>{p.projectName ?? p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth required label="Contractor"
                value={form.contractorId || ''}
                onChange={(e) => set('contractorId', e.target.value)}
              >
                {contractors.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required multiline rows={3} label="Description"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CurrencyInput
                fullWidth required label="Contract Value"
                value={form.contractValue || null}
                onChange={(v) => set('contractValue', v ?? 0)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth label="Status"
                value={form.status ?? 'ACTIVE'}
                onChange={(e) => set('status', e.target.value)}
              >
                {['ACTIVE', 'SUSPENDED'].map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required type="date" label="Start Date"
                value={form.startDate}
                onChange={(e) => set('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="date" label="End Date"
                value={form.endDate ?? ''}
                onChange={(e) => set('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Notes"
                value={form.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate('/dashboard/contracts')} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit" variant="contained" size="large"
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={18} /> : undefined}
          >
            {mutation.isPending ? 'Creating...' : 'Create Contract'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
